/**
 * DeleteConfirmationModal.tsx
 * Modal for confirming instrument deletion with usage analysis
 * 
 * Displays usage breakdown and handles both archive and hard delete operations.
 * Supports single and bulk operations with clear user feedback about consequences.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Modal, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter,
  Button,
  Alert,
  Chip,
  Listbox,
  ListboxItem,
  Spinner
} from '@enfomentis/ui';
import { Trash2, Archive, X } from 'lucide-react';
import type { InstrumentUsageResult } from '@/types/transient/InstrumentUsage';

/**
 * Props for the DeleteConfirmationModal component
 */
interface DeleteConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  
  /** Handler for closing the modal */
  onClose: () => void;
  
  /** Usage analysis results for instruments to be deleted */
  instruments: InstrumentUsageResult[];
  
  /** Handler for confirming the delete operation */
  onConfirm: (instruments: InstrumentUsageResult[]) => Promise<void>;
  
  /** Whether the operation is currently processing */
  isProcessing?: boolean;
  
  /** Current processing step for progress display */
  processingStep?: string;
  
  /** Processing progress percentage (0-100) */
  progressPercentage?: number;

  /** Whether usage data is still being loaded */
  isLoadingUsage?: boolean;
}

/**
 * Modal for confirming instrument deletion with intelligent usage analysis
 */
export const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
  isOpen,
  onClose,
  instruments,
  onConfirm,
  isProcessing = false,
  processingStep,
  progressPercentage: _progressPercentage,
  isLoadingUsage = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayedInstruments, setDisplayedInstruments] = useState<InstrumentUsageResult[]>([]);

  // Initialize and reset displayed instruments when modal opens/closes or instruments change
  useEffect(() => {
    if (isOpen && instruments.length > 0) {
      setDisplayedInstruments([...instruments]);
    } else if (!isOpen) {
      setIsSubmitting(false);
      setDisplayedInstruments([]);
    }
  }, [isOpen, instruments]);

  // Separate displayed instruments by action type
  const { archiveItems, deleteItems, totalCount } = useMemo(() => {
    const archiveItems = displayedInstruments.filter(inst => inst.recommendedAction === 'archive');
    const deleteItems = displayedInstruments.filter(inst => inst.recommendedAction === 'hard-delete');
    
    return {
      archiveItems,
      deleteItems,
      totalCount: displayedInstruments.length
    };
  }, [displayedInstruments]);

  // Handle confirm action
  const handleConfirm = async () => {
    if (isSubmitting || isProcessing || displayedInstruments.length === 0) return;
    
    setIsSubmitting(true);
    try {
      await onConfirm(displayedInstruments);
      onClose();
    } catch (error) {
      console.error('Delete operation failed:', error);
      // Error handling will be improved with toast notifications
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle removing an instrument from the list
  const handleRemoveInstrument = (instrumentId: string) => {
    setDisplayedInstruments(prev => 
      prev.filter(inst => inst.instrumentId !== instrumentId)
    );
  };

  // Generate usage description for list items
  const getUsageDescription = (instrument: InstrumentUsageResult): string => {
    if (instrument.totalUsageCount === 0) {
      return "No usage found";
    }
    
    const usageParts = [];
    const { tradeExecutions, marketStates, feeVerifications } = instrument.usageBreakdown;
    
    if (tradeExecutions.length > 0) {
      usageParts.push(`${tradeExecutions.length} trade${tradeExecutions.length > 1 ? 's' : ''}`);
    }
    if (marketStates.length > 0) {
      usageParts.push(`${marketStates.length} analysis`);
    }
    if (feeVerifications.length > 0) {
      usageParts.push(`${feeVerifications.length} fee record${feeVerifications.length > 1 ? 's' : ''}`);
    }
    
    return usageParts.join(', ');
  };

  // Don't render if no instruments initially provided
  if (!instruments || instruments.length === 0) {
    return null;
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onOpenChange={(open) => !open && !isProcessing && onClose()}
      size="2xl"
      placement="center"
      backdrop="blur"
      scrollBehavior="inside"
      hideCloseButton={isProcessing}
      isDismissable={!isProcessing}
      disableAnimation={false}
      shouldBlockScroll={false}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Trash2 size={20} className="text-danger" />
            <h2 className="text-lg font-semibold">Delete {totalCount} Instrument{totalCount > 1 ? 's' : ''}</h2>
          </div>
          <p className="text-sm text-foreground-500 font-normal">
            Review the action that will be taken for each instrument
          </p>
        </ModalHeader>

        <ModalBody>
          {isLoadingUsage ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-3">
                <Spinner size="lg" />
                <p className="text-sm text-foreground-600">Analyzing instrument usage...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status Summary */}
              <div className="flex items-center justify-between">
                <h3 className="text-medium font-semibold">Action Summary</h3>
                <div className="flex gap-2">
                  {totalCount === 0 ? (
                    <Chip size="sm" color="default" variant="flat">
                      No instruments selected
                    </Chip>
                  ) : (
                    <>
                      {archiveItems.length > 0 && (
                        <Chip size="sm" color="warning" variant="flat">
                          {archiveItems.length} Will be Archived
                        </Chip>
                      )}
                      {deleteItems.length > 0 && (
                        <Chip size="sm" color="danger" variant="flat">
                          {deleteItems.length} Will be Deleted
                        </Chip>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Empty state message */}
              {totalCount === 0 && (
                <Alert
                  color="default"
                  variant="flat"
                  title="No Instruments to Delete"
                >
                  You have removed all instruments from the delete list. Click Cancel to close this modal.
                </Alert>
              )}

              {/* Items to Archive Section */}
              {archiveItems.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Archive size={16} className="text-warning" />
                    <h4 className="font-medium text-warning">Items to be Archived ({archiveItems.length})</h4>
                  </div>
                  <p className="text-sm text-foreground-600 ml-6">
                    Will be hidden but preserved in your trading history
                  </p>
                  
                  <div className="border border-warning-200 rounded-lg">
                    <Listbox
                      aria-label="Instruments to be archived"
                      selectionMode="none"
                      className="max-h-48 overflow-y-auto"
                    >
                      {archiveItems.map((instrument) => (
                        <ListboxItem
                          key={instrument.instrumentId}
                          textValue={`${instrument.instrumentSymbol} - ${instrument.instrumentName}`}
                          startContent={<div className="w-2 h-2 rounded-full bg-warning"></div>}
                          endContent={
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => handleRemoveInstrument(instrument.instrumentId)}
                              className="text-foreground-400 hover:text-danger min-w-6 h-6"
                              aria-label="Remove from list"
                            >
                              <X size={12} />
                            </Button>
                          }
                          description={
                            <span className="text-text-secondary">
                              {getUsageDescription(instrument)}
                            </span>
                          }
                          className="data-[hover=true]:bg-transparent"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-text-primary">
                              {instrument.instrumentSymbol} - {instrument.instrumentName}
                            </span>
                          </div>
                        </ListboxItem>
                      ))}
                    </Listbox>
                  </div>
                </div>
              )}

              {/* Items to Delete Section */}
              {deleteItems.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Trash2 size={16} className="text-danger" />
                    <h4 className="font-medium text-danger">Items to be Permanently Deleted ({deleteItems.length})</h4>
                  </div>
                  <p className="text-sm text-foreground-600 ml-6">
                    Will be completely removed - no usage found
                  </p>
                  
                  <div className="border border-danger-200 rounded-lg">
                    <Listbox
                      aria-label="Instruments to be permanently deleted"
                      selectionMode="none"
                      className="max-h-48 overflow-y-auto text-text-secondary"
                    >
                      {deleteItems.map((instrument) => (
                        <ListboxItem
                          key={instrument.instrumentId}
                          textValue={`${instrument.instrumentSymbol} - ${instrument.instrumentName}`}
                          startContent={<div className="w-2 h-2 rounded-full bg-danger"></div>}
                          endContent={
                            <Button
                              size="sm"
                              variant="light"
                              isIconOnly
                              onPress={() => handleRemoveInstrument(instrument.instrumentId)}
                              className="text-foreground-400 hover:text-danger min-w-6 h-6"
                              aria-label="Remove from list"
                            >
                              <X size={12} />
                            </Button>
                          }
                          description={
                            <span className="text-text-secondary">
                              {getUsageDescription(instrument)}
                            </span>
                          }
                          className="data-[hover=true]:bg-transparent"
                        >
                          <div className="flex flex-col">
                            <span className="font-medium text-text-primary">
                              {instrument.instrumentSymbol} - {instrument.instrumentName}
                            </span>
                          </div>
                        </ListboxItem>
                      ))}
                    </Listbox>
                  </div>
                </div>
              )}

              {/* Warning Alert for Permanent Deletes */}
              {deleteItems.length > 0 && (
                <Alert
                  color="danger"
                  variant="bordered"
                  title="Permanent Deletion Warning"
                >
                  {deleteItems.length} instrument{deleteItems.length > 1 ? 's' : ''} will be permanently removed and cannot be recovered. This action is irreversible.
                </Alert>
              )}

              {/* Processing Progress */}
              {isProcessing && (
                <div className="flex items-center justify-center gap-3 py-4 border-t border-divider">
                  <Spinner size="sm" />
                  <div className="text-sm">
                    <span className="font-medium">Processing...</span>
                    {processingStep && (
                      <span className="text-foreground-600 ml-2">{processingStep}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            variant="light"
            onPress={onClose}
            isDisabled={isProcessing || isSubmitting}
          >
            Cancel
          </Button>
          <Button
            color="danger"
            onPress={handleConfirm}
            isLoading={isSubmitting || isProcessing}
            isDisabled={isLoadingUsage || totalCount === 0}
            startContent={!isSubmitting && !isProcessing ? <Trash2 size={16} /> : undefined}
          >
            {isLoadingUsage 
              ? 'Analyzing...'
              : totalCount === 0
                ? 'Nothing to Delete'
                : archiveItems.length > 0 && deleteItems.length > 0
                  ? `Archive ${archiveItems.length} & Delete ${deleteItems.length}`
                  : archiveItems.length > 0
                    ? `Archive ${archiveItems.length} Instrument${archiveItems.length > 1 ? 's' : ''}`
                    : `Delete ${deleteItems.length} Instrument${deleteItems.length > 1 ? 's' : ''}`
            }
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};